import { ErrorBoundary } from '@sentry/react';
import { Typography } from '@statsbomb/kitbag-components';
import { Atom, useAtomValue } from 'jotai';
import { Link } from 'react-router-dom';
import { playerLinkAtom, playerPreviewLink } from '@/atoms/navigation';
import { unwrappedPlayerIdMappingAtom } from '@/atoms/queries/players';
import { userNavOverridesAtom } from '@/atoms/queries/user';
import { ClassicEntityLink } from '@/components/navigation/ClassicEntityLink';
import { TeamLink as BaseTeamLink } from '@/components/navigation/TeamLink';
import useGetLinkPath from '@/hooks/useGetLinkPath';
import { LinkObject } from '@/types/generic';
import { LinkConfig } from '@/types/navigation';
import { classicEntityDashboardUrl } from '@/utils/classicLinks';

const TableLink = ({
  ids,
  label,
  linkConfigAtom,
}: {
  ids: Record<string, string | number>;
  label: LinkObject['label'];
  linkConfigAtom: Atom<LinkConfig>;
}) => {
  const { to, relative } = useGetLinkPath(linkConfigAtom, ids);
  return (
    <Typography as={Link} variant="linkMedium" to={to} relative={relative}>
      {label}
    </Typography>
  );
};

export const TeamLinkTable = ({ id, label }: LinkObject) => <BaseTeamLink id={id} label={label} variant="linkMedium" />;

export const PlayerLink = ({ id, label }: LinkObject) => {
  const isOldPlayerXp = useAtomValue(userNavOverridesAtom).player;
  const playersMapping = useAtomValue(unwrappedPlayerIdMappingAtom);
  const iqId = playersMapping[id];
  const classicPlayerHref = classicEntityDashboardUrl('player', iqId);

  // When a page scopes the link to the preview (player scout and the event data locker), always
  // open the preview — even in classic mode. The preview's "View Dashboard" button then honours the
  // classic vs new experience, so the classic override must not short-circuit the entry point here.
  const isPreviewLink = useAtomValue(playerLinkAtom).path === playerPreviewLink.path;

  return isOldPlayerXp && !isPreviewLink ? (
    <ClassicEntityLink href={classicPlayerHref} variant="linkMedium">
      {label}
    </ClassicEntityLink>
  ) : (
    <ErrorBoundary fallback={<Typography variant="linkMedium">{label}</Typography>}>
      <TableLink ids={{ playerId: id }} label={label} linkConfigAtom={playerLinkAtom} />
    </ErrorBoundary>
  );
};
